import './member-group.component.css';

import { AddressComponent, DependentAddressComponent, MemberComponent } from './member.component';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Card, Heading, IconButton, Pane, SelectField, TextInputField, majorScale } from 'evergreen-ui';
import { Client, MemberGroup, Plan, createValidationContext, memberGroupSchema, memberGroupSchemaV2 } from '../models';
import { CoverageTier, Relationship } from '../models/enums';
import { Form, FormItem, FormValidationMessages, propsForValue } from '../controllers/form.controller';
import React, { FC } from 'react';
import { compareDesc, isFuture, isSameDay, parseISO } from 'date-fns';

import { ActiveBadge } from './active-badge.component';
import { FiTrash } from 'react-icons/fi';
import { MemberGroupActionsComponent } from './member-group-actions.component';
import { SaveMemberGroupQuery } from './save-member-group-query.component';
import { SsnDatabase } from '../ssn-db';
import { ValidationMessagesComponent } from './validation-messages.component';
import { getTimeFrameForPlan } from '../helpers/plan-timeframe-helper';
import { useEditMemberGroupController } from '../controllers/edit-member-group.controller';
import { useFlags } from '../feature-flags/main';

const MemberCard = React.forwardRef<
  HTMLDivElement,
  {
    title: React.ReactNode;
    titleLeft?: React.ReactNode;
    titleRight?: React.ReactNode;
    children: React.ReactNode;
  }
>(({ title, titleLeft, titleRight, children }, ref) => (
  <Card
    ref={ref}
    elevation={1}
    border
    display="flex"
    flexDirection="row"
    gap={8}
    flexWrap="wrap"
    padding={majorScale(1)}
    background="white"
  >
    <Pane display="flex" flexDirection="row" alignItems="center" gap={8} flex="100%" paddingBottom={majorScale(1)}>
      {titleLeft ? (
        <Pane is="aside" flex="0 0 auto">
          {titleLeft}
        </Pane>
      ) : null}
      <Heading size={500} flex="1">
        {title}
      </Heading>
      {titleRight ? (
        <Pane is="aside" flex="0 0 auto">
          {titleRight}
        </Pane>
      ) : null}
    </Pane>
    {children}
  </Card>
));

function memberCardTitle({ relationshipCode }: { relationshipCode: Relationship }) {
  switch (relationshipCode) {
    case Relationship.SELF:
      return 'Employee';
    case Relationship.SPOUSE:
      return 'Spouse';
    case Relationship.CHILD:
      return 'Child';
  }
}

function getTimeFramesToPlans(plans: Plan[]) {
  const timeFramesToPlans = new Map<string, Plan[]>();
  for (const plan of plans) {
    const timeFrame = getTimeFrameForPlan(plan);
    if (!timeFramesToPlans.has(timeFrame)) {
      timeFramesToPlans.set(timeFrame, []);
    }
    const planIds = timeFramesToPlans.get(timeFrame);
    planIds?.push(plan);
  }
  return timeFramesToPlans;
}

function timeFrameCompareFn(a: [string, Plan[]], b: [string, Plan[]]) {
  const aKey = a[0];
  const bKey = b[0];
  const aStartDate = new Date(aKey.substring(1).split(' - ')[0]);
  const bStartDate = new Date(bKey.substring(1).split(' - ')[0]);
  return compareDesc(aStartDate, bStartDate);
}

interface MemberGroupFieldsComponentProps {
  plans: Plan[];
  onTimeFrameChanged: (timeFrame: string, plans: Plan[] | undefined) => void;
  onPlanChanged: (plan: Plan) => void;
  onCoverageTierChanged: (coverageTier: CoverageTier) => void;
  onInvoiceGroupChanged: (invoiceGroup: string) => void;
}

const MemberGroupFieldsComponent: FC<MemberGroupFieldsComponentProps> = ({
  plans,
  onTimeFrameChanged,
  onPlanChanged,
  onCoverageTierChanged,
  onInvoiceGroupChanged,
}) => {
  const planIdProps = propsForValue('benefitPlanId');
  const timeFramesToPlans = getTimeFramesToPlans(plans);
  const timeFrames = [...timeFramesToPlans];

  timeFrames.sort(timeFrameCompareFn);
  const latestTimeFrame = timeFrames[0]?.[0];

  const timeFrameProps = propsForValue('benefitPlanTimeFrame');
  const selectedTimeFrame = timeFrameProps?.value ?? latestTimeFrame;
  const plansForTimeFrame = timeFramesToPlans.get(selectedTimeFrame) ?? [];

  React.useEffect(() => {
    if (!timeFrameProps?.value) {
      onTimeFrameChanged(latestTimeFrame, timeFramesToPlans.get(latestTimeFrame));
    }
  }, [timeFrameProps?.value]);

  return (
    <>
      <Card flex="100%" display="flex" gap="8px">
        <SelectField
          {...timeFrameProps}
          onChange={e => onTimeFrameChanged(e.target.value, timeFramesToPlans.get(e.target.value))}
          size="small"
          flex="calc(60% - 4px)"
        >
          {!timeFrameProps?.value ? <option>No time frame selected</option> : null}
          {timeFrames.map(([tf]) => (
            <option key={tf} value={tf}>
              {tf}
            </option>
          ))}
        </SelectField>
        <SelectField
          {...planIdProps}
          onChange={e => onPlanChanged(plans.find(p => p.id === Number(e.target.value))!)}
          size="small"
          flex="calc(60% - 4px)"
        >
          {!planIdProps?.value ? <option>No plan selected</option> : null}
          {plansForTimeFrame.map(plan => (
            <option key={plan.id} value={plan.id}>
              {`${plan.name}`}
            </option>
          ))}
        </SelectField>
      </Card>
      <Card flex="100%" display="flex" gap="8px">
        <SelectField
          flex="calc(40% - 4px)"
          {...propsForValue('coverageTier')}
          onChange={e => onCoverageTierChanged(e.target.value as CoverageTier)}
        >
          <option value={CoverageTier.EMPLOYEE}>Employee</option>
          <option value={CoverageTier.EMPLOYEE_SPOUSE}>Employee + Spouse</option>
          <option value={CoverageTier.EMPLOYEE_CHILDREN}>Employee + Children</option>
          <option value={CoverageTier.FAMILY}>Family</option>
        </SelectField>
        <TextInputField
          {...propsForValue('invoiceGroup')}
          onChange={(e: React.ChangeEvent<{ value: string }>) => onInvoiceGroupChanged(e.target.value)}
          width="30%"
        />
      </Card>
    </>
  );
};

const primaryFields: (string | number)[] = ['', 'primary', 'coverageTier', 'planId'];

export interface MemberGroupComponentProps {
  client: Client;
  ssnDB: SsnDatabase;
  memberGroup?: MemberGroup;
  onClose?: () => void;
  onIsDirtyChanged?: (isDirty: boolean) => void;
  onSaveComplete?: (memberGroupId: string) => void | Promise<void>;
  onSaveError?: (error: any) => void;
}

export const MemberGroupComponent: FC<MemberGroupComponentProps> = props => {
  const { 'membership-single-source-of-truth-in-eligibility': membershipSingleSourceOfTruthInEligibility } = useFlags();
  const [state, dispatch] = useEditMemberGroupController(props);
  const focusRef = React.createRef<HTMLDivElement>();
  React.useEffect(() => {
    if (state.focusDependentIndex != null) {
      focusRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state.focusDependentIndex]);

  const validationContext = React.useCallback(createValidationContext, []);

  React.useEffect(() => props.onIsDirtyChanged?.(!!state.isDirty), [props.onIsDirtyChanged, state.isDirty]);
  return (
    <Form
      value={state}
      schema={membershipSingleSourceOfTruthInEligibility ? memberGroupSchemaV2 : memberGroupSchema}
      validationContext={validationContext(
        state.ssnDB,
        state.plan?.startDate,
        state.plan?.endDate,
        state.primary.benefitStartDate,
        state.primary.benefitEndDate,
        state.isNew,
        membershipSingleSourceOfTruthInEligibility,
      )}
      isNew={state.isNew}
    >
      <Pane display="flex" flexDirection="row" flexWrap="wrap" gap={majorScale(1)}>
        <SaveMemberGroupQuery
          plans={props.client.plans}
          isNew={state.isNew}
          isDirty={state.isDirty}
          onSaveComplete={props.onSaveComplete}
          onSaveError={props.onSaveError}
        >
          {outProps => (
            <MemberGroupActionsComponent
              {...state}
              {...outProps}
              canCancel={state.isNew && state.isDirty}
              dispatch={action => {
                if (action.type === 'cancel') props.onClose?.();
                else dispatch(action);
              }}
            />
          )}
        </SaveMemberGroupQuery>

        <MemberCard
          title={memberCardTitle(state.primary)}
          titleRight={
            <ActiveBadge
              active={
                (!state.primary.benefitEndDate ||
                  isFuture(parseISO(state.primary.benefitEndDate)) ||
                  isSameDay(parseISO(state.primary.benefitEndDate), Date.now())) &&
                !state.primary.isCancelTerminated
              }
            />
          }
        >
          <FormValidationMessages
            filter={([name], { isTouched, isNew }) => (!isNew || isTouched) && primaryFields.includes(name)}
          >
            {({ warnings, errors }) => (
              <>
                <ValidationMessagesComponent type="warnings" messages={warnings} />
                <ValidationMessagesComponent type="errors" messages={errors} />
              </>
            )}
          </FormValidationMessages>
          <MemberGroupFieldsComponent
            plans={props.client.plans}
            onTimeFrameChanged={(timeFrame, plans) => dispatch({ type: 'updatePlanTimeFrame', timeFrame, plans })}
            onPlanChanged={plan => dispatch({ type: 'updatePlan', plan })}
            onCoverageTierChanged={coverageTier => dispatch({ type: 'updateCoverageTier', coverageTier })}
            onInvoiceGroupChanged={invoiceGroup => dispatch({ type: 'updateInvoiceGroup', invoiceGroup })}
          />
          <FormItem path={['primary']}>
            <MemberComponent
              isPrimary={true}
              planEndDate={state.plan?.endDate}
              onChange={(field, value) => {
                if (
                  membershipSingleSourceOfTruthInEligibility &&
                  (field === 'benefitStartDate' || field === 'benefitEndDate')
                ) {
                  if (field === 'benefitStartDate') {
                    dispatch({ type: 'updatePrimaryBenefitStartDate', value });
                  } else if (field === 'benefitEndDate') {
                    dispatch({ type: 'updatePrimaryBenefitEndDate', value });
                  }
                } else {
                  dispatch({ type: 'updatePrimary', field, value });
                }
              }}
              onTerminate={() => dispatch({ type: 'terminateMemberGroup' })}
              onCancelTerminate={() => dispatch({ type: 'cancelTerminateMemberGroup' })}
              onRemoveCancelTerminate={() => dispatch({ type: 'updatePrimaryBenefitEndDate', value: null })}
            />
            <AddressComponent onChange={(field, value) => dispatch({ type: 'updatePrimary', field, value })} />
          </FormItem>
        </MemberCard>

        {/* component=null so it doesn't make an extra <div> messing up our Pane gap */}
        <TransitionGroup component={null}>
          {state.dependents.map((dep, index) => {
            const dependentIsActive =
              (!dep.benefitEndDate ||
                isFuture(parseISO(dep.benefitEndDate)) ||
                isSameDay(parseISO(dep.benefitEndDate), Date.now())) &&
              !dep.isCancelTerminated;
            return (
              <CSSTransition key={dep.id} classNames="dependent-card" timeout={500}>
                <MemberCard
                  ref={index === state.focusDependentIndex ? focusRef : null}
                  title={memberCardTitle(dep)}
                  titleLeft={
                    dep.isNew ? (
                      <IconButton
                        icon={<FiTrash />}
                        intent="danger"
                        onClick={() => dispatch({ type: 'deleteDependent', index })}
                      />
                    ) : null
                  }
                  titleRight={<ActiveBadge active={dependentIsActive} />}
                >
                  <FormItem path={['dependents', index]} isNew={dep.isNew}>
                    {dependentIsActive && (
                      <FormValidationMessages
                        filter={([key, i], { isTouched, isNew }) =>
                          (!isNew || isTouched) && key === 'dependents' && i === index
                        }
                      >
                        {({ warnings, errors }) => (
                          <>
                            <ValidationMessagesComponent type="warnings" messages={warnings} />
                            <ValidationMessagesComponent type="errors" messages={errors} />
                          </>
                        )}
                      </FormValidationMessages>
                    )}

                    <MemberComponent
                      isPrimary={false}
                      isPrimaryCancelTerminated={state.primary.isPastTerminated || state.primary.isCancelTerminated}
                      planEndDate={state.plan?.endDate}
                      onChange={(field, value) => {
                        if (
                          membershipSingleSourceOfTruthInEligibility &&
                          (field === 'benefitStartDate' || field === 'benefitEndDate')
                        ) {
                          if (field === 'benefitStartDate') {
                            dispatch({ type: 'updateDependentBenefitStartDate', index, value });
                          } else if (field === 'benefitEndDate') {
                            dispatch({ type: 'updateDependentBenefitEndDate', index, value });
                          }
                        } else {
                          dispatch({ type: 'updateDependent', index, field, value });
                        }
                      }}
                      onTerminate={() => dispatch({ type: 'terminateDependent', index })}
                      onCancelTerminate={() => dispatch({ type: 'cancelTerminateDependent', index })}
                      onRemoveCancelTerminate={() =>
                        dispatch({ type: 'updateDependentBenefitEndDate', index, value: null })
                      }
                    />

                    <DependentAddressComponent
                      onChangeIsAddressSameAsPrimary={value =>
                        dispatch({ type: 'updateDependentAddressIsSameAsPrimary', index, value })
                      }
                      onChange={(field, value) => dispatch({ type: 'updateDependent', index, field, value })}
                    />
                  </FormItem>
                </MemberCard>
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </Pane>
    </Form>
  );
};
